import { format } from '@monofront/business/utils';
import { useCheckoutCartState } from '@monofront/business/contexts';
import { useTranslations } from 'next-intl';
import { useFeatureValue } from '@monofront/business/contexts';
import {
  DELIVERY_FEES,
  REFRESHED_DELIVERY,
} from '@monofront/business/constants';
import ProgressBar from '@monofront/design-system/components/ProgressBar';
import Tooltip from '@monofront/design-system/components/Tooltip';
import { useMemo } from 'react';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import { format as formatPrice } from '@monofront/business/utils';
import { useHasReachedFreeDelivery } from '@hooks/businessContainer';

interface IProgressFreeDeliveryProps {
  totalPrice: number;
  shouldHideWhenComplete?: boolean;
}

const ProgressFreeDelivery: React.FC<IProgressFreeDeliveryProps> = ({
  totalPrice,
  shouldHideWhenComplete = false,
}) => {
  const tUltraFresh = useTranslations('UltraFresh');
  const { shoppingCart } = useShoppingCart();
  const { checkoutCart } = useCheckoutCartState();
  const tCart = useTranslations('Cart');
  const deliveryValues = useFeatureValue(...DELIVERY_FEES)?.[
    REFRESHED_DELIVERY
  ];

  const {
    amountUntilFreeDeliveryInCents,
    hasReachedFreeDelivery,
    freeDeliveryProgression,
  } = useHasReachedFreeDelivery();

  const Wrapper = useMemo(
    () =>
      ({ children }) =>
        shoppingCart?.hasUltraFreshProduct ? (
          <Tooltip
            ariaId="ultra-fresh-delivery-progress"
            UNSAFE_className="tw-w-[290px]"
            content={
              <>
                <p className="tw-lf-body-bold-md tw-mb-1">
                  {tUltraFresh('deliveryNoticeTitle')}
                </p>
                <ul className="tw-lf-body-regular-md tw-ml-5">
                  {deliveryValues.map(({ aboveAmount, underAmount, price }) => {
                    const formattedPrice = formatPrice(price / 100);

                    return (
                      <>
                        {+price === 0 && aboveAmount ? (
                          <li className="tw-list-disc">
                            {tUltraFresh.rich('deliveryNoticeFreeFromAmount', {
                              above: formatPrice(aboveAmount / 100),
                            })}
                          </li>
                        ) : null}
                        {aboveAmount && underAmount ? (
                          <li className="tw-list-disc">
                            {tUltraFresh.rich('deliveryNoticeBetweenAmount', {
                              price: formattedPrice,
                              above: formatPrice(aboveAmount / 100),
                              under: formatPrice(underAmount / 100),
                            })}
                          </li>
                        ) : null}
                        {+price !== 0 && aboveAmount && !underAmount ? (
                          <li className="tw-list-disc">
                            {tUltraFresh.rich('deliveryNoticeAboveAmount', {
                              price: formattedPrice,
                              above: formatPrice(aboveAmount / 100),
                            })}
                          </li>
                        ) : null}
                        {!aboveAmount && underAmount ? (
                          <li className="tw-list-disc">
                            {tUltraFresh.rich('deliveryNoticeUnderAmount', {
                              price: formattedPrice,
                              under: formatPrice(underAmount / 100),
                            })}
                          </li>
                        ) : null}
                      </>
                    );
                  })}
                </ul>
                <p className="tw-lf-body-regular-md tw-mt-6">
                  {tUltraFresh('deliveryNoticeCondition')}
                </p>
              </>
            }
            position="bottom"
            arrowPosition="center"
            eventType="click-only"
          >
            {children}
          </Tooltip>
        ) : (
          children
        ),
    [shoppingCart.hasUltraFreshProduct],
  );

  if (
    checkoutCart?.withMembership === false ||
    !totalPrice ||
    (shouldHideWhenComplete && hasReachedFreeDelivery)
  ) {
    return null;
  }

  return (
    <Wrapper>
      <div className="tw-space-y-2">
        <div className="tw-flex tw-items-center tw-justify-center">
          <p className="tw-mb-1 tw-text-center">
            {hasReachedFreeDelivery
              ? tCart('freeDeliveryUnlocked')
              : (shoppingCart.hasUltraFreshProduct ? tUltraFresh : tCart).rich(
                  'freeDeliveryProgress',
                  {
                    amount: format(amountUntilFreeDeliveryInCents / 100),
                    span: (children) => (
                      <span className="tw-font-semibold">{children}</span>
                    ),
                  },
                )}
            {shoppingCart.hasUltraFreshProduct && (
              <span
                className="hzb tw-mb-1 tw-ml-2 tw-inline-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-full tw-bg-black-1000 tw-text-white"
                aria-hidden
              >
                {'?'}
              </span>
            )}
          </p>
        </div>
        <ProgressBar currentProgress={freeDeliveryProgression} hasBackground />
      </div>
    </Wrapper>
  );
};

export default ProgressFreeDelivery;
